import type { FilterByOpts, ListURLParams, QueryRouteBarrelTypes } from '@core/api';
import { DEFAULT_LOOKUP_LENGTH } from '@core/constants/common';
import Enums from '@core/types/api/Enums';

/**
 * Helper type to extract the entity type from a barrel's ListURLParams.
 * Used to infer the type parameter for ConstructListQueryParams from the barrel.
 */
export type ExtractEntityTypeFromBarrel<
  QKey extends keyof QueryRouteBarrelTypes,
  QBarrel extends QueryRouteBarrelTypes[QKey] = QueryRouteBarrelTypes[QKey],
> = QBarrel['args'] extends { queryParams?: ListURLParams<infer T> }
  ? T
  : QBarrel['args'] extends { queryParams: ListURLParams<infer T> }
    ? T
    : {};

type QueryParams<T extends {}> = {
  queryParams: ListURLParams<T>;
};

/**
 * Builds a lookup's list query: the trimmed input text becomes a `Lookup` filter, combined
 * with the given filters and paging.
 */
export const ConstructListQueryParams = <T extends {} = {}>(
  inputText: string,
  queryParams?: Partial<ListURLParams<T>>,
): QueryParams<T> => {
  const { page, perPage, sortBy, filterBy = [], columns, isDistinct } = queryParams ?? {};

  const trimmedInput = inputText.trim();
  const lookupFilter: FilterByOpts<T>[] =
    trimmedInput !== ''
      ? [{ operation: Enums.FilterOperation.Lookup, values: [trimmedInput] }]
      : [];

  const combinedFilterBy = [...lookupFilter, ...filterBy];

  return {
    queryParams: {
      page: page ?? 0,
      perPage: perPage ?? DEFAULT_LOOKUP_LENGTH,
      ...(combinedFilterBy.length > 0 && { filterBy: combinedFilterBy }),
      ...(sortBy !== undefined && { sortBy }),
      ...(columns !== undefined && { columns }),
      ...(isDistinct !== undefined && { isDistinct }),
    },
  };
};
